import React from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/compact';

import moment from 'moment-timezone';

import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_PROPERTY_OWNER_INSTITUTION,
} from 'common/listingConstants';
import {
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_ANY_TIME,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE,
  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
} from 'common/marketingPartnerListingConstants';

import { makeListingFullAddressString } from 'common/listingHelpers';

import { formatPrice } from 'common/formatters';

import SrCard, {
  SR_CARD_BULLET_COLOR_YELLOW,
  SR_CARD_BULLET_COLOR_BLUE,
} from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import SrInfoTile from 'client/components/SrInfoTile/SrInfoTile';
import SrSeparator from 'client/components/SrSeparator';

const SrLocalInfoTile = ({ marginTop = true, value, ...props }) => (
  <SrBox marginTop={marginTop ? '21px' : '0px'}>
    <SrInfoTile value={Array.isArray(value) ? lodashCompact(value).join('\n') : value} {...props} />
  </SrBox>
);

const SrLocalSeparator = ({ marginTop = true }) => (
  <SrBox marginTop={marginTop ? '17px' : '0px'}>
    <SrSeparator />
  </SrBox>
);

const SrSunrisePropertySummary = ({ formikBag: { values }, onEditStep, partner }) => {
  const _getPropertySecondOwnerDetail = () => {
    return lodashCompact([
      values.propertySecondOwnerName,
      values.propertySecondOwnerPhone,
      values.propertySecondOwnerEmail,
    ]).join(', ');
  };

  const _hasPropertySecondOwner = () => {
    return (
      lodashCompact([
        values.propertySecondOwnerName,
        values.propertySecondOwnerPhone,
        values.propertySecondOwnerEmail,
      ]).length > 0
    );
  };

  const hasPropertySecondOwner = _hasPropertySecondOwner();
  const propertySecondOwnerDetail = _getPropertySecondOwnerDetail();

  return (
    <>
      <SrCard color={SR_CARD_BULLET_COLOR_YELLOW} header="Order Details">
        <SrLocalInfoTile marginTop label="Service" value="SunSetup&trade; Property Setup" />
        {(values.requiresMarketingItems || !values.isAlreadyActive) && (
          <SrLocalInfoTile
            marginTop
            valueColor="#00A238"
            label="Earliest install date"
            value={moment(values.availableForSetupDate).format('dddd, MMM Do')}
          />
        )}
      </SrCard>
      <SrCard color={SR_CARD_BULLET_COLOR_BLUE} header="Property">
        <SrLocalInfoTile
          label="Address"
          value={makeListingFullAddressString(values)}
          onEditClick={() => onEditStep('address')}
        />
        <SrLocalInfoTile
          label="Property Owner"
          value={
            values.propertyOwnerType === LISTING_PROPERTY_OWNER_INSTITUTION
              ? values.propertyOwnerName
              : `${values.propertyOwnerName}, ${values.propertyOwnerPhone}, ${values.propertyOwnerEmail}`
          }
          onEditClick={() => onEditStep('propertyOwner')}
        />
        {hasPropertySecondOwner && (
          <SrLocalInfoTile
            label="Second Property Owner"
            value={propertySecondOwnerDetail}
            onEditClick={() => onEditStep('propertyOwner')}
          />
        )}
        <SrLocalInfoTile
          label="Rent Price"
          value={values.shouldRunCMA ? 'Run a CMA' : `${formatPrice(values.price)}`}
          onEditClick={() => onEditStep('price')}
        />
        <SrLocalSeparator />
        <SrLocalInfoTile
          label="Occupant Status"
          value={lodashCompact([
            values.occupantType,
            values.occupantType !== LISTING_OCCUPANT_TYPE_VACANT &&
              [values.occupantName, values.occupantPhone].join(', '),
            values.occupantEmail,
          ])}
          onEditClick={() => onEditStep('occupant')}
        />
        <SrLocalSeparator />
        {(values.requiresMarketingItems || !values.isAlreadyActive) && (
          <SrLocalInfoTile
            label="Earliest Install Date"
            value={moment(values.availableForSetupDate).format('dddd, MMM Do')}
            onEditClick={() => onEditStep('availability')}
          />
        )}
        <SrLocalInfoTile
          label="Earliest move in date"
          value={moment(values.availableDate).format('dddd, MMM Do')}
          onEditClick={() => onEditStep('availability')}
        />
        {(values.requiresMarketingItems || values.requirePhotos || !values.isAlreadyActive) && (
          <SrLocalInfoTile
            label="Put on market date"
            value={moment(values.releaseToMarketDate).format('dddd, MMM Do')}
            onEditClick={() => onEditStep('availability')}
          />
        )}
        <SrLocalSeparator />

        <SrLocalInfoTile
          label="Key or Code access ready"
          value={[
            PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS.find(
              ({ value }) => value === values.accessMethod,
            )?.label,
            `"${values.keyPickupDescription}"`,
          ]}
          onEditClick={() => onEditStep('access')}
        />

        <SrLocalInfoTile
          label="Combo code"
          value={values.lockboxCode ? values.lockboxCode : 'None'}
          onEditClick={() => onEditStep('access')}
        />
        <SrLocalInfoTile
          label="Gate Code"
          value={values.gateCode ? values.gateCode : 'None'}
          onEditClick={() => onEditStep('access')}
        />
        <SrLocalInfoTile
          label="Alarm Code"
          value={values.alarmCode ? values.alarmCode : 'None'}
          onEditClick={() => onEditStep('access')}
        />
        <SrLocalSeparator />
        {partner?.experienceConfiguration?.additionalMarketingDocsEnabled && (
          <>
            <SrLocalInfoTile
              label="Marketing Documents"
              value={
                values.additionalMarketingDocs.length > 0
                  ? `${values.additionalMarketingDocs.length} Uploaded`
                  : 'None'
              }
              onEditClick={() => onEditStep('uploadMarketingDocs')}
            />
            <SrLocalSeparator />
          </>
        )}
        {(values.requiresMarketingItems || !values.isAlreadyActive) && (
          <>
            <SrLocalInfoTile
              label="Yard Sign"
              value={values.yardSignAllowed ? 'Allowed' : 'Not allowed'}
              onEditClick={() => onEditStep('marketingItems')}
            />

            <SrLocalInfoTile
              label="Self-Tour Hardware"
              value={values.requireSelfShowingHardware ? 'Yes install' : 'Do not install'}
              onEditClick={() => onEditStep('marketingItems')}
            />
          </>
        )}
        {(values.requiresPhotos || !values.isAlreadyActive) && (
          <SrLocalInfoTile
            label="Photography"
            value={
              values.requirePhotos
                ? lodashCompact([
                    'Take photos',
                    values.availableForPhotoDate &&
                      // eslint-disable-next-line max-len
                      `Earliest date is ${moment(values.availableForPhotoDate).format('MMM Do')}`,
                    values.availableForPhotoDateSchedule ===
                      AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_ANY_TIME && 'Go any time of day',
                    values.availableForPhotoDateSchedule ===
                      AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE && 'Call to schedule',
                  ])
                : ['Do not take photos', values.linkToExistingPhotos && 'Link to photos given']
            }
            onEditClick={() => onEditStep('marketingItems')}
          />
        )}
        {values.requirePhotos &&
          values.availableForPhotoDateSchedule ===
            AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE && (
            <SrLocalInfoTile
              label="Scheduling Contact"
              value={[values.availableForPhotoContactName, values.availableForPhotoContactPhone]}
              onEditClick={() => onEditStep('marketingItems')}
            />
          )}
        {(values.informationCompleteness || values.marketingInfoRequiredFromOwner) && (
          <>
            <SrLocalSeparator />
            <SrLocalInfoTile
              label="Extra marketing info needed?"
              value={
                values.informationCompleteness ? 'None' : values.marketingInfoRequiredFromOwner
              }
              onEditClick={() => onEditStep('extraMarketingInfo')}
            />
          </>
        )}
        {values.leaseReferralFee && (
          <>
            <SrLocalSeparator />
            <SrLocalInfoTile
              label="Lease Referral Fee"
              value={`${formatPrice(values.leaseReferralFee)}`}
              onEditClick={() => onEditStep('referralFee')}
            />
          </>
        )}
      </SrCard>
    </>
  );
};

SrSunrisePropertySummary.propTypes = {
  formikBag: PropTypes.object.isRequired,
};

SrSunrisePropertySummary.displayName = 'SrSunrisePropertySummary';

export default SrSunrisePropertySummary;
